import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { useSupabaseAuth } from '../context/AuthContext';
import useWorkspaceAccess from '../hooks/useWorkspaceAccess';
import Badge from '../components/Badge';
import Loader from '../components/Loader';
import Modal from '../components/Modal';
import OwnerSearch from '../components/OwnerSearch';
import { approveRequirementChange, DECISION_TYPE_LABEL } from '../lib/requirementChangeControl';

const DEC_STATUS = {
  made: { label: 'Made', variant: 'gr' },
  pending: { label: 'Pending', variant: 'am' },
  proposed: { label: 'Proposed', variant: 'am' },
  rejected: { label: 'Rejected', variant: 'rd' },
  deferred: { label: 'Deferred', variant: '' },
};
const ACT_STATUS = {
  open: { label: 'Open', variant: '' },
  due_today: { label: 'Due today', variant: 'am' },
  overdue: { label: 'Overdue', variant: 'rd' },
  in_progress: { label: 'In progress', variant: 'am' },
  done: { label: 'Done', variant: 'gr' },
  cancelled: { label: 'Cancelled', variant: '' },
};

const BLANK_DEC = { title: '', decided_by_name: '', status: 'proposed', owner_id: null, owner_name: '' };
const BLANK_ACT = { title: '', owner_name: '', owner_id: null, due_date: '', status: 'open' };

function actionDisplayStatus(action) {
  if (action.status === 'done' || action.status === 'cancelled') return action.status;
  if (!action.due_date) return action.status === 'in_progress' ? 'in_progress' : 'open';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${action.due_date}T00:00:00`);
  if (due < today) return 'overdue';
  if (due.getTime() === today.getTime()) return 'due_today';
  return action.status === 'in_progress' ? 'in_progress' : 'open';
}

function ownerLabel(row) {
  return row.profiles?.full_name || row.owner_name || row.decided_by_name || 'TBC';
}

export default function Decisions() {
  const { project, programme } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();

  const [decisions, setDecisions] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function approveChangeRequest(decision) {
    setSaving(true);
    try {
      await approveRequirementChange(decision.id, project.id, profile);
      await load();
    } catch (e) {
      console.error('[Decisions] approve change failed', e);
    } finally {
      setSaving(false);
    }
  }

  const load = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const [dRes, aRes] = await Promise.all([
        sb.from('governance_decisions')
          .select('*, owner:profiles!governance_decisions_owner_id_fkey(full_name)')
          .eq('project_id', project.id)
          .order('created_at', { ascending: false }),
        sb.from('governance_actions')
          .select('*, owner:profiles!governance_actions_owner_id_fkey(full_name)')
          .eq('project_id', project.id)
          .order('created_at', { ascending: false }),
      ]);
      setDecisions(dRes.data || []);
      setActions(aRes.data || []);
    } catch (e) {
      console.error('[Decisions] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => ({
    overdue: actions.filter((a) => actionDisplayStatus(a) === 'overdue').length,
    open: actions.filter((a) => !['done', 'cancelled'].includes(a.status)).length,
  }), [actions]);

  function openAdd(kind) {
    setForm(kind === 'decision' ? { ...BLANK_DEC } : { ...BLANK_ACT });
    setError(null);
    setModal({ kind, mode: 'add' });
  }

  async function saveModal() {
    const title = form.title?.trim();
    if (!title) return;
    setSaving(true);
    setError(null);

    if (modal.kind === 'decision') {
      const status = form.status || 'proposed';
      const payload = {
        programme_id: programme.id,
        project_id: project.id,
        title,
        decided_by_name: form.decided_by_name?.trim() || ownerLabel(form) || null,
        owner_id: form.owner_id || null,
        status,
        decision_date: status === 'made' ? new Date().toISOString().slice(0, 10) : null,
        created_by: profile?.id || null,
      };
      const { data, error: err } = await sb
        .from('governance_decisions')
        .insert(payload)
        .select('*, owner:profiles!governance_decisions_owner_id_fkey(full_name)')
        .single();
      if (err) { setError(err.message); setSaving(false); return; }
      setDecisions((prev) => [data, ...prev]);
    } else {
      const payload = {
        programme_id: programme.id,
        project_id: project.id,
        title,
        owner_id: form.owner_id || null,
        owner_name: form.owner_name?.trim() || null,
        due_date: form.due_date || null,
        status: form.status || 'open',
        created_by: profile?.id || null,
      };
      const { data, error: err } = await sb
        .from('governance_actions')
        .insert(payload)
        .select('*, owner:profiles!governance_actions_owner_id_fkey(full_name)')
        .single();
      if (err) { setError(err.message); setSaving(false); return; }
      setActions((prev) => [data, ...prev]);
    }

    setSaving(false);
    setModal(null);
  }

  if (loading) return <div className="content"><Loader text="Loading decisions…" /></div>;

  return (
    <div className="content">
      <div className="chdr" style={{ marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 500, color: '#2c2c2a' }}>Decisions & actions</div>
          <div style={{ fontSize: 11, color: '#888780', marginTop: 2 }}>
            {project?.name || 'Project'} · Governance
          </div>
        </div>
        <div className="badges">
          {summary.overdue > 0 && <Badge variant="am">{summary.overdue} overdue action{summary.overdue !== 1 ? 's' : ''}</Badge>}
          <Badge>{summary.open} open</Badge>
        </div>
      </div>

      <div className="r2">
        <div className="card">
          <div className="chdr">
            <span className="ctitle">Decision log</span>
            {canWrite && <button type="button" className="cact" onClick={() => openAdd('decision')}>+ Add decision</button>}
          </div>
          {decisions.length === 0 ? (
            <p style={{ padding: '8px 0', fontSize: 11, color: '#888780' }}>No decisions recorded yet.</p>
          ) : (
            decisions.map((d) => {
              const st = DEC_STATUS[d.status] || DEC_STATUS.proposed;
              const by = ownerLabel(d);
              const meta = d.status === 'made'
                ? `Made by: ${by}${d.decision_date ? ` · ${new Date(d.decision_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}`
                : `Owner: ${by} · Pending approval`;
              return (
                <div key={d.id} className="ir">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: '#2c2c2a', fontWeight: 500 }}>
                      {d.ref} — {d.title}
                      {d.decision_type && (
                        <span style={{ marginLeft: 8, fontSize: 9, color: '#3C3489' }}>
                          {DECISION_TYPE_LABEL[d.decision_type] || d.decision_type}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: '#888780', marginTop: 1 }}>{meta}</div>
                    {d.description && (
                      <div style={{ fontSize: 11, color: '#5f5e5a', marginTop: 4 }}>{d.description}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {canWrite && d.decision_type === 'requirement_change' && ['pending', 'proposed'].includes(d.status) && (
                      <button type="button" className="btn-ghost" style={{ fontSize: 10 }} disabled={saving}
                        onClick={() => approveChangeRequest(d)}>Approve unlock</button>
                    )}
                    <Badge variant={st.variant} style={{ fontSize: 10 }}>{st.label}</Badge>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="card">
          <div className="chdr">
            <span className="ctitle">Action log</span>
            {canWrite && <button type="button" className="cact" onClick={() => openAdd('action')}>+ Add action</button>}
          </div>
          {actions.length === 0 ? (
            <p style={{ padding: '8px 0', fontSize: 11, color: '#888780' }}>No actions tracked yet.</p>
          ) : (
            actions.map((a) => {
              const display = actionDisplayStatus(a);
              const st = ACT_STATUS[display] || ACT_STATUS.open;
              const dueLabel = a.due_date
                ? new Date(a.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                : '—';
              return (
                <div key={a.id} className="ir">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: '#2c2c2a' }}>{a.title}</div>
                    <div style={{ fontSize: 11, color: '#888780', marginTop: 1 }}>Owner: {ownerLabel(a)} · Due {dueLabel}</div>
                  </div>
                  <Badge variant={st.variant} style={{ fontSize: 10 }}>{st.label}</Badge>
                </div>
              );
            })
          )}
        </div>
      </div>

      {modal && (
        <Modal
          title={modal.kind === 'decision' ? 'Add decision' : 'Add action'}
          onClose={() => setModal(null)}
          busy={saving}
          footer={(
            <>
              <button type="button" className="btn-ghost" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
              <button type="button" className="btn-primary" onClick={saveModal} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </>
          )}
        >
          {error && <div className="toast-error" style={{ marginBottom: 10 }}>{error}</div>}
          {modal.kind === 'decision' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label className="form-label">Title<input className="form-input" value={form.title || ''} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></label>
              <label className="form-label">Made by / owner (text)<input className="form-input" value={form.decided_by_name || ''} onChange={(e) => setForm((f) => ({ ...f, decided_by_name: e.target.value }))} /></label>
              <div className="form-row">
                <label className="form-label">Profile owner (optional)</label>
                <OwnerSearch
                  displayName={form.owner_name || ''}
                  onChange={(id, name) => setForm((f) => ({ ...f, owner_id: id, owner_name: name }))}
                />
              </div>
              <label className="form-label">Status
                <select className="form-select" value={form.status || 'proposed'} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                  <option value="proposed">Proposed</option>
                  <option value="pending">Pending approval</option>
                  <option value="made">Made</option>
                </select>
              </label>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label className="form-label">Action<input className="form-input" value={form.title || ''} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></label>
              <label className="form-label">Owner (text)<input className="form-input" value={form.owner_name || ''} onChange={(e) => setForm((f) => ({ ...f, owner_name: e.target.value }))} /></label>
              <div className="form-row">
                <label className="form-label">Profile owner (optional)</label>
                <OwnerSearch
                  displayName={form.owner_name || ''}
                  onChange={(id, name) => setForm((f) => ({ ...f, owner_id: id, owner_name: name }))}
                />
              </div>
              <label className="form-label">Due date<input type="date" className="form-input" value={form.due_date || ''} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} /></label>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
